import React, { useState, useEffect } from 'react';
import { Check, Send } from 'lucide-react';
import { Sheet, Avatar, plural } from './AdminUi';

// Zajednički panel „Pošalji obavijest” (zadatak, Oglasna ploča): primatelji po skupinama uz pojedinačno
// odznačavanje, poruka s preporučenim tekstovima, zadnje slanje u podnožju. Slanje radi pozivatelj (onSend).

export function agoText(iso) {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'upravo';
  if (min < 60) return `prije ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `prije ${h} h`;
  return `prije ${Math.round(h / 24)} d`;
}

export default function NotifySheet({
  title = 'Pošalji obavijest', subtitle, sources, defaultSrc, templates, last, lastLabel = 'Zadnja obavijest',
  note, emptyText, onSend, onSent, onClose, z = 1300,
}) {
  const [src, setSrc] = useState(null);           // Set ključeva skupina
  const [off, setOff] = useState(new Set());      // odznačeni pojedinci
  const [text, setText] = useState('');
  const [tmpl, setTmpl] = useState(null);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const closeRef = React.useRef(null);

  // Početne skupine kad se popis učita; prvi preporučeni tekst.
  useEffect(() => {
    if (src !== null || !sources.length) return;
    setSrc(new Set(defaultSrc));
  }, [sources, src, defaultSrc]);
  useEffect(() => {
    if (tmpl !== null || !templates.length) return;
    setTmpl(templates[0].key);
    setText(templates[0].text);
  }, [templates, tmpl]);

  const active = src || new Set();
  const placed = new Map();   // email → ključ skupine u kojoj se prikazuje
  for (const s of sources) if (active.has(s.key)) for (const p of s.people) if (!placed.has(p.email)) placed.set(p.email, s.key);
  const chosen = [...placed.keys()].filter(e => !off.has(e));

  const toggleSrc = (key) => { setTouched(true); setSrc(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; }); };
  const togglePerson = (email) => { setTouched(true); setOff(prev => { const n = new Set(prev); n.has(email) ? n.delete(email) : n.add(email); return n; }); };
  const setSection = (emails, on) => { setTouched(true); setOff(prev => { const n = new Set(prev); emails.forEach(e => (on ? n.delete(e) : n.add(e))); return n; }); };

  const n = chosen.length;
  const missing = !n ? 'Odaberi primatelje' : !text.trim() || text.trim() === 'Promjena:' ? 'Upiši poruku' : null;

  const send = async () => {
    if (missing || busy) return;
    setBusy(true); setError('');
    try {
      const audience = sources.filter(s => active.has(s.key)).map(s => s.label).join(' + ');
      const sent = await onSend(chosen, text.trim(), audience);
      onSent && onSent(sent);
    } catch (err) {
      setError(err.response?.data?.error || 'Slanje nije uspjelo.');
      setBusy(false);
    }
  };

  return (
    <Sheet tall z={z} onClose={onClose} dirty={touched} busy={busy} closeRef={closeRef}
      discardText="Obavijest još nije poslana."
      head={
        <div className="adm-sh">
          <div className="adm-main">
            <h3>{title}</h3>
            {subtitle && <p>{subtitle}</p>}
          </div>
        </div>
      }
      footer={
        <div className="adm-dirty">
          {last?.at && (
            <p className="tn-last">{lastLabel}: {agoText(last.at)}, {last.count} {plural(last.count, 'osoba', 'osobe', 'osoba')}{last.by_name ? ` (${last.by_name})` : ''}</p>
          )}
          {error && <p style={{ color: '#ac463b' }}>{error}</p>}
          <div className="tf-foot">
            <button type="button" className="adm-btn" disabled={busy} onClick={() => closeRef.current && closeRef.current()}>Odustani</button>
            <button type="button" className={`adm-btn ${missing ? 'tf-off' : 'primary'}`} disabled={!!missing || busy} onClick={send}>
              {busy ? 'Šaljem…' : missing || <><Send size={16} /> Pošalji ({n})</>}
            </button>
          </div>
        </div>
      }
    >
      <div className="adm-lbl">Kome</div>
      {sources.length === 0 ? (
        <p className="adm-note">{emptyText}</p>
      ) : (
        <div className="tf-chips">
          {sources.map(s => {
            const on = active.has(s.key);
            const partial = on && s.people.some(p => off.has(p.email) && placed.get(p.email) === s.key);
            return (
              <button key={s.key} type="button" className={`tf-chip${on ? (partial ? ' tn-part' : ' on') : ''}`} onClick={() => toggleSrc(s.key)} aria-pressed={on}>
                {on && <Check size={14} strokeWidth={3} />} {s.label} <i className="tn-n">{s.people.length}</i>
              </button>
            );
          })}
        </div>
      )}

      {placed.size > 0 && (
        <div className="adm-card">
          {sources.filter(s => active.has(s.key)).map(s => {
            const people = s.people.filter(p => placed.get(p.email) === s.key);
            if (!people.length) return null;
            const onCount = people.filter(p => !off.has(p.email)).length;
            const allOn = onCount === people.length;
            return (
              <React.Fragment key={s.key}>
                <div className="tn-sec">
                  <span>{s.label} · {onCount}/{people.length}</span>
                  <button type="button" onClick={() => setSection(people.map(p => p.email), !allOn)}>{allOn ? 'nitko' : 'svi'}</button>
                </div>
                {people.map(p => {
                  const on = !off.has(p.email);
                  return (
                    <button key={p.email} type="button" className={`adm-it${on ? '' : ' tn-off'}`} onClick={() => togglePerson(p.email)}>
                      <Avatar name={p.name} size={32} />
                      <div className="adm-main">
                        <div className="adm-t soft" style={{ fontSize: 15.5 }}>{p.name}</div>
                        {p.sub && <div className="adm-s">{p.sub}</div>}
                      </div>
                      <span className={`adm-check${on ? ' on' : ''}`}>{on && <Check size={15} strokeWidth={3} />}</span>
                    </button>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      )}

      <div className="adm-lbl">Poruka</div>
      {templates.length > 1 && (
        <div className="tn-tmpl">
          {templates.map(t => (
            <button key={t.key} type="button" className={tmpl === t.key ? 'on' : ''} onClick={() => { setTmpl(t.key); setText(t.text); setTouched(true); }}>{t.label}</button>
          ))}
        </div>
      )}
      <textarea id="tn-text" className="adm-input tn-text" rows={3} maxLength={500} value={text}
        onChange={e => { setText(e.target.value); setTmpl('custom'); setTouched(true); }} />
      {note && <p className="adm-note">{note}</p>}
    </Sheet>
  );
}
